import { JsonEditor } from 'json-edit-react';
import useSchemaValidation from './hooks/useSchemaValidation';
import { useState } from 'react';
import { Input } from '@headlessui/react';
import {
    getAutoFixValue,
    canAutoFix,
    getAutoFixDescription,
} from './utils/autoFixDefaults';

interface ExperienceEditorProps {
    screens: ScreenDefinition[];
    definition: Definition;
    onScreenSelected: (screenId: string) => void;
    onDefinitionChange: (definition: Definition) => void;
    warnings?: string[];
    errors?: string[];
    activationId: number;
}

export default function ExperienceEditor({
    screens,
    definition,
    onScreenSelected,
    onDefinitionChange,
    warnings: legacyWarnings = [],
    errors: legacyErrors = [],
    activationId,
}: ExperienceEditorProps) {
    // Use the same schema validation as the visual editor
    const [searchText, setSearchText] = useState<string>('');
    const { validationResult } = useSchemaValidation(activationId, {
        enabled: true,
    });

    // Handle navigating to error location in JSON
    const handleNavigateToPath = (path: string) => {
        // Extract the last part of the path to make it searchable
        // e.g., "screens[0].stage.metadata.prompt" -> "prompt"
        const searchTerm = path.split('.').pop() || path;
        setSearchText(searchTerm);
    };

    // Apply auto-fix to the definition
    const handleAutoFix = (path: string, value: any) => {
        const updatedDefinition = JSON.parse(JSON.stringify(definition));

        // Parse the path and navigate to set the value
        const pathParts = path.split(/[.[\]]/).filter((p) => p);
        let current = updatedDefinition;

        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            const nextPart = pathParts[i + 1];

            // Handle array indices
            if (!isNaN(Number(part))) {
                current = current[Number(part)];
            } else {
                // Handle object keys
                if (!current[part]) {
                    // Create missing intermediate objects/arrays
                    current[part] = !isNaN(Number(nextPart)) ? [] : {};
                }
                current = current[part];
            }
        }

        // Set the final value
        const lastPart = pathParts[pathParts.length - 1];
        if (!isNaN(Number(lastPart))) {
            current[Number(lastPart)] = value;
        } else {
            if (value === null) {
                // Remove the field if value is null (for videoProcessingFlowId)
                delete current[lastPart];
            } else {
                current[lastPart] = value;
            }
        }

        onDefinitionChange(updatedDefinition);
    };

    // Debug logging
    console.log('ExperienceEditor - validationResult:', validationResult);

    return (
        <div className="relative h-full">
            <div className="w-full mx-auto flex justify-center h-full pb-[180px]">
                <div className="w-full mx-auto flex-col justify-center">
                    <div className="w-full flex justify-center">
                        <div className="w-full mx-auto flex justify-center"></div>
                        <div>
                            <Input
                                type="text"
                                value={searchText}
                                onChange={(e: any) =>
                                    setSearchText(e.target.value)
                                }
                                className="w-full mx-auto flex justify-center border border-border rounded-md p-2 m-2"
                                placeholder="Search"
                            />
                        </div>
                    </div>
                    <JsonEditor
                        data={definition}
                        className="w-[calc(100vw)]"
                        maxWidth="85vw"
                        searchFilter="key"
                        searchText={searchText}
                        onUpdate={(data) => {
                            console.log('data', data);
                            onDefinitionChange(
                                data.newData as unknown as Definition,
                            );
                        }}
                    />
                </div>
            </div>

            {/* Validation Status Bar - Positioned above toolbar */}
            <div className="fixed bottom-[70px] left-0 right-0 bg-background border-t border-border shadow-lg z-[250]">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-foreground">
                            Validation Status:
                        </span>

                        {validationResult &&
                            validationResult.errors.length > 0 && (
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 rounded-full bg-red-500" />
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {validationResult.errors.length} error
                                        {validationResult.errors.length !== 1
                                            ? 's'
                                            : ''}
                                    </span>
                                </div>
                            )}

                        {validationResult &&
                            validationResult.warnings.length > 0 && (
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        {validationResult.warnings.length}{' '}
                                        warning
                                        {validationResult.warnings.length !== 1
                                            ? 's'
                                            : ''}
                                    </span>
                                </div>
                            )}

                        {(!validationResult ||
                            (validationResult.errors.length === 0 &&
                                validationResult.warnings.length === 0)) && (
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                <span className="text-sm text-muted-foreground">
                                    No issues found
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="text-xs text-muted-foreground">
                        {validationResult && validationResult.errors.length > 0
                            ? 'Fix errors to ensure iOS app compatibility'
                            : validationResult &&
                              validationResult.warnings.length > 0
                            ? 'Review warnings to improve experience'
                            : 'Definition is valid'}
                    </div>
                </div>

                {/* Error Details */}
                {validationResult && validationResult.errors.length > 0 && (
                    <div className="border-t border-border px-4 py-2 bg-red-50 max-h-60 overflow-y-auto">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-red-800">
                                Errors (click path to search):
                            </div>
                            {validationResult.errors.some((error) =>
                                canAutoFix(
                                    error.path,
                                    error.message,
                                    definition,
                                ),
                            ) && (
                                <button
                                    onClick={() => {
                                        validationResult.errors.forEach(
                                            (error) => {
                                                const fixValue =
                                                    getAutoFixValue(
                                                        error.path,
                                                        error.message,
                                                        definition,
                                                    );
                                                if (fixValue !== undefined) {
                                                    handleAutoFix(
                                                        error.path,
                                                        fixValue,
                                                    );
                                                }
                                            },
                                        );
                                    }}
                                    className="flex items-center gap-1 text-white bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1 rounded transition-colors font-medium"
                                    title="Auto-fix all fixable errors"
                                >
                                    🔧 Fix All
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {validationResult.errors.map((error, index) => {
                                const autoFixValue = getAutoFixValue(
                                    error.path,
                                    error.message,
                                    definition,
                                );
                                const isAutoFixable = canAutoFix(
                                    error.path,
                                    error.message,
                                    definition,
                                );
                                const fixDescription = getAutoFixDescription(
                                    error.path,
                                );

                                return (
                                    <div
                                        key={index}
                                        className="text-xs bg-background rounded border border-red-200 p-2"
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <button
                                                onClick={() =>
                                                    handleNavigateToPath(
                                                        error.path,
                                                    )
                                                }
                                                className="flex items-center gap-1 text-red-600 hover:text-red-800 font-mono text-xs bg-red-100 px-2 py-1 rounded hover:bg-red-200 transition-colors cursor-pointer"
                                                title="Click to search for this field in JSON"
                                            >
                                                <span>{error.path}</span>
                                            </button>
                                            {isAutoFixable && (
                                                <button
                                                    onClick={() =>
                                                        handleAutoFix(
                                                            error.path,
                                                            autoFixValue,
                                                        )
                                                    }
                                                    className="flex items-center gap-1 text-white bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1 rounded transition-colors font-medium"
                                                    title={`Auto-fix: Set to ${JSON.stringify(
                                                        autoFixValue,
                                                    )}`}
                                                >
                                                    🔧 Fix
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-red-700">
                                            {error.message}
                                        </div>
                                        {error.suggestion && (
                                            <div className="text-red-600 mt-1 italic">
                                                💡 {error.suggestion}
                                            </div>
                                        )}
                                        {isAutoFixable && (
                                            <div className="text-blue-600 mt-1.5 text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
                                                <span className="font-medium">
                                                    ⚡ Auto-fix:
                                                </span>{' '}
                                                {fixDescription}
                                                {autoFixValue !== null && (
                                                    <code className="ml-1 bg-blue-100 px-1 py-0.5 rounded">
                                                        {JSON.stringify(
                                                            autoFixValue,
                                                        )}
                                                    </code>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Warning Details */}
                {validationResult &&
                    validationResult.warnings.length > 0 &&
                    validationResult.errors.length === 0 && (
                        <div className="border-t border-border px-4 py-2 bg-yellow-50 max-h-60 overflow-y-auto">
                            <div className="text-xs font-semibold text-yellow-800 mb-2">
                                Warnings (click path to search):
                            </div>
                            <div className="space-y-2">
                                {validationResult.warnings.map(
                                    (warning, index) => (
                                        <div
                                            key={index}
                                            className="text-xs bg-background rounded border border-yellow-200 p-2"
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                <button
                                                    onClick={() =>
                                                        handleNavigateToPath(
                                                            warning.path,
                                                        )
                                                    }
                                                    className="flex items-center gap-1 text-yellow-600 hover:text-yellow-800 font-mono text-xs bg-yellow-100 px-2 py-1 rounded hover:bg-yellow-200 transition-colors cursor-pointer"
                                                    title="Click to search for this field in JSON"
                                                >
                                                    <span>{warning.path}</span>
                                                </button>
                                            </div>
                                            <div className="text-yellow-700">
                                                {warning.message}
                                            </div>
                                            {warning.suggestion && (
                                                <div className="text-yellow-600 mt-1 italic">
                                                    💡 {warning.suggestion}
                                                </div>
                                            )}
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
}

export function ScreenCard({ children }: { children: React.ReactNode }) {
    return (
        <div className="overflow-hidden bg-[#EEEFEF] shadow-md sm:rounded-lg max-w-[280px]">
            <div className="flex flex-col items-center justify-center px-4 py-5 sm:p-6  h-[400px]">
                {children}
            </div>
        </div>
    );
}
